import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import WorkEnvironmentModal from "../components/WorkEnvironmentModal";

const STORAGE_KEY = "mes.workEnvironment.v1";

export type WorkEnvironment = {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  vanName: string;
  dikYn: "Y" | "N";
  serialCableCount?: number;
};

type WorkEnvironmentContextValue = {
  workEnvironment: WorkEnvironment | null;
  setWorkEnvironment: (env: WorkEnvironment | null) => void;
  openWorkEnvironmentModal: () => void;
  closeWorkEnvironmentModal: () => void;
  isModalOpen: boolean;
};

const WorkEnvironmentContext = createContext<WorkEnvironmentContextValue | null>(
  null,
);

const readStored = (): WorkEnvironment | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorkEnvironment;
    if (!parsed?.terminalModelId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const persist = (env: WorkEnvironment | null) => {
  try {
    if (!env) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(env));
  } catch {
    /* ignore */
  }
};

export const WorkEnvironmentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [workEnvironment, setWorkEnvironmentState] =
    useState<WorkEnvironment | null>(() => readStored());
  const [isModalOpen, setIsModalOpen] = useState(false);

  const setWorkEnvironment = useCallback((env: WorkEnvironment | null) => {
    setWorkEnvironmentState(env);
    persist(env);
  }, []);

  const openWorkEnvironmentModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeWorkEnvironmentModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      workEnvironment,
      setWorkEnvironment,
      openWorkEnvironmentModal,
      closeWorkEnvironmentModal,
      isModalOpen,
    }),
    [
      workEnvironment,
      setWorkEnvironment,
      openWorkEnvironmentModal,
      closeWorkEnvironmentModal,
      isModalOpen,
    ],
  );

  return (
    <WorkEnvironmentContext.Provider value={value}>
      {children}
      <WorkEnvironmentModal
        opened={isModalOpen}
        onClose={closeWorkEnvironmentModal}
        current={workEnvironment}
        onConfirm={setWorkEnvironment}
      />
    </WorkEnvironmentContext.Provider>
  );
};

export const useWorkEnvironment = () => {
  const ctx = useContext(WorkEnvironmentContext);
  if (!ctx) {
    throw new Error(
      "useWorkEnvironment must be used within WorkEnvironmentProvider",
    );
  }
  return ctx;
};

export const formatWorkEnvironmentLabel = (env: WorkEnvironment) =>
  `${env.internalModelName} · ${env.vanName}`;
